import { useMemo, useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import Card from '@/components/base/Card';
import Badge from '@/components/base/Badge';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import FileUploadModal from './components/FileUploadModal';
import { fileCategories } from '@/types';
import { useData } from '@/context/DataContext';
import { useApp } from '@/context/AppContext';
import { supabase, FILES_BUCKET } from '@/lib/supabase';
import { formatDate } from '@/lib/format';

const categoryIcons: Record<string, string> = {
  Teklif: 'ri-file-list-3-line',
  'Elektrik projesi': 'ri-lightbulb-line',
  'Teknik çizim': 'ri-draft-line',
  'PLC dosyası': 'ri-cpu-line',
  HMI: 'ri-tv-line',
  'Makine fotoğrafı': 'ri-camera-line',
  Video: 'ri-video-line',
  'Fatura / belge': 'ri-file-text-line',
  'Servis belgesi': 'ri-file-list-3-line',
  Diğer: 'ri-file-3-line',
};

export default function Files() {
  const { files, customerName, projectName } = useData();
  const { openDelete, showToast } = useApp();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return files.filter((f) => {
      const matchesQuery = !q || f.name.toLowerCase().includes(q);
      const matchesCategory = !category || f.category === category;
      return matchesQuery && matchesCategory;
    });
  }, [query, category, files]);

  const handleDownload = async (storagePath: string, name: string) => {
    if (!storagePath) {
      showToast('Bu dosyanın içeriği bulunamadı');
      return;
    }
    const { data, error } = await supabase.storage.from(FILES_BUCKET).createSignedUrl(storagePath, 60);
    if (error || !data?.signedUrl) {
      showToast('İndirilemedi: ' + (error?.message ?? 'dosya bulunamadı'));
      return;
    }
    const a = document.createElement('a');
    a.href = data.signedUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="Dosyalar"
        description={`${filtered.length} dosya`}
        actions={
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-upload-2-line text-lg" /> Dosya Yükle
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Dosya adı ara..." className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Kategoriler</option>
          {fileCategories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Dosya bulunamadı" description="Filtreleri değiştirerek veya dosya yükleyerek başlayın." />
      ) : (
        <div className="stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((f) => (
            <Card key={f.id} className="flex items-center gap-3 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={categoryIcons[f.category] ?? 'ri-file-3-line'} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground-900">{f.name}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{f.size} · {formatDate(f.uploadedDate)}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <Badge>{f.category}</Badge>
                  <span className="text-[11px] text-foreground-400">
                    {f.relatedType === 'Müşteri' ? customerName(f.relatedId) : projectName(f.relatedId)}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => handleDownload(f.storagePath, f.name)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
                >
                  <i className="ri-download-2-line" />
                </button>
                <RowActions onDelete={() => openDelete('file', f.id, f.name)} />
              </div>
            </Card>
          ))}
        </div>
      )}

      <FileUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}